// =============================================================================
// PEDRA — src/components/PdfDownloadButton.tsx
// Botao "Baixar PDF" da barra do resultado: gera o arquivo do plano de corte
// e baixa direto — no computador e no celular, sem passar pela caixa de
// impressao do navegador.
//
// Os diagramas do PDF sao desenhados pelo MESMO <SheetDiagram> da tela, numa
// raiz React avulsa, fora do documento, uma chapa por vez: o PDF sai com as
// camadas que estao ligadas agora (rotulos, cotas, cortes, fitas, cores) e
// nao depende de quantas chapas cabem no bloco de impressao.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import type { EdgeBandType, OptimizationResult, Settings } from '../core/types';
import { downloadPdfReport } from '../lib/pdfReport';
import { SheetDiagram } from './SheetDiagram';

/** Camadas do diagrama, as mesmas dos checkboxes embaixo dele. */
export interface DiagramLayers {
  showLabels: boolean;
  showDimensions: boolean;
  showCuts: boolean;
  showEdgeBanding: boolean;
  colorFilled: boolean;
}

export interface PdfDownloadButtonProps {
  projectName: string;
  result: OptimizationResult;
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
  layers: DiagramLayers;
  /** true quando as entradas mudaram depois do calculo: o PDF leva o aviso. */
  stale?: boolean;
  disabled?: boolean;
  onNotify?(tone: 'success' | 'danger', text: string): void;
}

/** Largura de referencia do SVG avulso; o PDF reescala em vetor. */
const PDF_DIAGRAM_WIDTH = 900;

export function PdfDownloadButton(props: PdfDownloadButtonProps): ReactElement {
  const { projectName, result, settings, edgeBands, layers, stale = false, disabled = false, onNotify } = props;
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const generate = useCallback((): void => {
    if (typeof document === 'undefined') return;
    const host = document.createElement('div');
    let root: Root | null = null;
    try {
      root = createRoot(host);
      const r = root;
      const filename = downloadPdfReport({
        projectName,
        result,
        settings,
        edgeBands,
        stale,
        renderDiagram: (sheet) => {
          flushSync(() => {
            r.render(
              <SheetDiagram
                key={sheet.index}
                sheet={sheet}
                settings={settings}
                showLabels={layers.showLabels}
                showDimensions={layers.showDimensions}
                showCuts={layers.showCuts}
                showEdgeBanding={layers.showEdgeBanding}
                colorFilled={layers.colorFilled}
                edgeBands={edgeBands}
                maxWidth={PDF_DIAGRAM_WIDTH}
              />,
            );
          });
          return host.querySelector('svg');
        },
      });
      onNotify?.('success', `PDF baixado: ${filename}`);
    } catch {
      onNotify?.('danger', 'Não foi possível gerar o PDF. Tente de novo.');
    } finally {
      root?.unmount();
    }
  }, [projectName, result, settings, edgeBands, layers, stale, onNotify]);

  const handleClick = useCallback((): void => {
    if (busy) return;
    setBusy(true);
    // Um respiro para o "Gerando…" aparecer antes do trabalho pesado — num
    // celular com muitas chapas a montagem leva uma fração de segundo.
    window.setTimeout(() => {
      try {
        generate();
      } finally {
        if (mounted.current) setBusy(false);
      }
    }, 30);
  }, [busy, generate]);

  return (
    <button
      type="button"
      className="btn btn--sm btn--primary res-pdf-btn"
      onClick={handleClick}
      disabled={disabled || busy}
      aria-busy={busy}
      title="Baixar o plano de corte em PDF (desenho de cada chapa, peças, cortes e sobras)"
    >
      <span aria-hidden="true" className="res-pdf-icon">
        ⬇
      </span>
      <span>{busy ? 'Gerando PDF…' : 'Baixar PDF'}</span>
    </button>
  );
}

export default PdfDownloadButton;
